import { useEffect } from "react";
import { Turtle, ArrowLeft } from "lucide-react";
import { LEGAL } from "../mock/mock";
import { Button } from "./ui/button";
import { useConsent } from "../context/ConsentContext";

export default function CookiePolicy() {
  const { openPreferences } = useConsent();

  useEffect(() => {
    document.title = "Politique de cookies — Gérard Trèves, la Tortue de Compostelle";
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", "https://gerardlatortuedecompostel.fr/politique-cookies");
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#3d3a33]">
      <header className="border-b border-[#D8CCB9]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Retour à l'accueil">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25372D] text-[#D8CCB9]">
              <Turtle className="h-4 w-4" />
            </span>
            <span className="font-serif-display text-base font-semibold text-[#25372D]">
              La Tortue de Compostel'
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#806850] hover:text-[#25372D]"
          >
            <ArrowLeft className="h-4 w-4" /> Accueil
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <h1 className="font-serif-display text-4xl md:text-5xl font-semibold text-[#25372D]">
          Politique de cookies
        </h1>
        <p className="mt-4 text-sm text-[#857f72]">
          Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}
        </p>

        <div className="mt-8 space-y-8 leading-relaxed">
          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              Qu'est-ce qu'un cookie ?
            </h2>
            <p className="mt-3">
              Un cookie est un petit fichier déposé sur votre appareil lors de
              la visite d'un site. Il permet notamment de mémoriser des
              informations (comme vos préférences) ou de mesurer l'audience.
              Certains dispositifs de stockage local (localStorage) fonctionnent
              de manière similaire. Sur ce site, ces technologies ne sont
              utilisées que dans les limites décrites ci-dessous.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              Pourquoi ce site utilise-t-il des cookies ?
            </h2>
            <p className="mt-3">
              Pour assurer le bon fonctionnement du site, mémoriser vos choix de
              confidentialité, et — uniquement avec votre accord — mesurer
              l'audience et permettre l'affichage de contenus externes (vidéos
              YouTube).
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              1. Cookies strictement nécessaires
            </h2>
            <p className="mt-3">
              Toujours actifs — ils ne nécessitent pas de consentement et ne
              peuvent pas être désactivés.
            </p>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                <strong>Choix de cookies</strong> — stockage local
                (<code>cookie_consent_v1</code>), déposé par ce site pour
                mémoriser vos préférences de consentement. Finalité :
                fonctionnement. Fournisseur : ce site. Durée : 6 mois.
              </li>
              <li>
                <strong>Script technique de la plateforme</strong> —
                <code> emergent-main.js</code> (assets.emergent.sh), utilisé
                pour le fonctionnement technique. Il ne s'agit pas d'un cookie
                de suivi publicitaire.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              2. Mesure d'audience
            </h2>
            <p className="mt-3">
              Désactivée par défaut — chargée uniquement après votre accord.
            </p>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                <strong>PostHog</strong> — statistiques de fréquentation et
                d'utilisation du site. Fournisseur : PostHog
                (host : <code>us.i.posthog.com</code>). Cookies / stockage :
                identifiants de type <code>ph_*</code>. Durée : durée par défaut
                de PostHog (jusqu'à environ 12 mois).
                <span className="text-[#857f72]">
                  {" "}
                  (À vérifier selon la configuration exacte de PostHog.)
                </span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              3. Contenus externes
            </h2>
            <p className="mt-3">
              Désactivés par défaut — les vidéos ne sont chargées qu'après votre
              accord (un aperçu s'affiche à la place tant que vous n'avez pas
              autorisé).
            </p>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                <strong>YouTube</strong> — lecture des vidéos intégrées.
                Fournisseur : YouTube / Google. Lors de la lecture, YouTube peut
                déposer ses propres cookies. Les durées de conservation sont
                fixées par Google et varient (session à plusieurs mois).
                <span className="text-[#857f72]">
                  {" "}
                  (Cookies gérés par Google, hors du contrôle de ce site.)
                </span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              Comment accepter, refuser ou modifier vos choix ?
            </h2>
            <p className="mt-3">
              Lors de votre première visite, une bannière vous permet de{" "}
              <strong>tout accepter</strong>, <strong>tout refuser</strong> ou{" "}
              <strong>personnaliser</strong> vos choix. Aucun cookie soumis à
              consentement n'est déposé avant votre choix explicite. Vous pouvez
              à tout moment modifier ou retirer votre consentement — aussi
              facilement que vous l'avez donné — via le bouton ci-dessous ou le
              lien « Gérer mes cookies » présent dans le pied de page.
            </p>
            <Button
              data-testid="cookie-manage-policy"
              onClick={openPreferences}
              className="mt-5 rounded-full bg-[#25372D] text-white hover:bg-[#1b2a21]"
            >
              Gérer mes cookies
            </Button>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium text-[#25372D]">
              Contact
            </h2>
            <p className="mt-3">
              Pour toute question relative aux cookies :{" "}
              <a
                href={`mailto:${LEGAL.contactEmail}`}
                className="text-[#25372D] underline"
              >
                {LEGAL.contactEmail}
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
